import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useProduct} from '@montana/containers/product';
import {TabletLandscapeAndLarger, SmallerThanTabletLandscape} from '@montana/shared/responsive';
import {Translate} from '@montana/shared/translate';
import {formatString} from '@montana/helpers/string-format/string-format.helper';
import {Price} from '@montana/shared/price';
import {ITabProps, Tablist} from '@montana/shared/tablist/tablist.component';
import {ButtonDropdown} from '@montana/shared/dropdown';
import {useCart} from '@montana/containers/cart';
import {Button, ButtonStyles} from '@montana/shared/button';
import {getDimensionTranslationValue} from '@montana/helpers/product.helper';
import {useTranslations} from '@montana/containers/translations';
import {useTracking} from '@montana/containers/tracking';
import {IGtmProductWithQuantity} from '@montana/services/tracking/tracking';
import {getFindDealerUrl} from '@montana/helpers/find-dealer.helper';
import {useSettings} from '@montana/containers/settings/settings.container';
import {Splash} from '@montana/shared/splash/splash.component';
import {PdpSpecificationsTab, usePDPContext} from '../../product-page.context';
import {preferredDimensionOrder} from '../../variation-pickers/variation-pickers.component';
import {useDynamicSettings} from '@montana/containers/dynamic-settings';
import {staticSettings} from '@montana/helpers/static-settings.helper';
import {CUSTOMER_MODE} from "@montana/constants/customer-mode";
import clsx from 'clsx';
import {IProductRequest} from '@montana/models/generated/cms.interface';
import {IProductConfigOption} from '@montana/models/dev';
import {SvgIcon} from '@montana/shared/svg-icon';

interface IContactMontana {
    setContactMontanaModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const PriceSection = () => {

    const { prices, professionalsFromPrice } = useProduct();
    const { market, customerMode } = useSettings();

    if (customerMode === CUSTOMER_MODE.PROFESSIONAL)
    {
        const translate = useTranslations();
        return (
            <>
                {(!!professionalsFromPrice && !market?.hidePrices) && (
                    <>
                        <div className="product-information__price-display">
                            <p className="product-information__price-current">
                                {translate(t => t.productPage.professionalsProductPageFrom, '[From]')} <Price obj={professionalsFromPrice} />
                            </p>
                        </div>
                        {staticSettings.b2b &&
                            <p className="product-information__suggested-retail-price"><Translate value="general.suggestedRetailPrice" defaultValue="[Suggested retail price]" /></p>
                        }
                    </>
                )}
            </>
        );
    }
    return (
        <>
            {(!!prices && !market?.hidePrices) && (
                <>
                    <TabletLandscapeAndLarger>
                        <Splash {...prices.splash} />
                    </TabletLandscapeAndLarger>
                    <div className="product-information__price-display">
                        <p className="product-information__price-current"><Price obj={prices.priceWithDiscount} /></p>
                        {!!prices.savedAmount?.amount && (
                            <p className="product-information__price-before">
                                <span><Translate value="productPage.productPageBefore" defaultValue="[Before]" /> </span>
                                <Price className="price" obj={prices.beforePrice} />
                            </p>
                        )}
                    </div>
                    {staticSettings.b2b &&
                        <p className="product-information__suggested-retail-price"><Translate value="general.suggestedRetailPrice" defaultValue="[Suggested retail price]" /></p>
                    }
                    {!!prices.savedAmount?.amount && (
                        <p className="product-information__price-saved">
                            <span><Translate value="productPage.productPageSave" defaultValue="[Save]" />&nbsp;</span>
                            <Price obj={prices.savedAmount} />
                        </p>
                    )}
                </>
            )}
        </>
    );
};

const DispatchTimeSection = () => {

    const { prices } = useProduct();
    const { market } = useSettings();

    return (
        <>
            {(!!prices?.dispatchTime && !market?.hidePrices && !market?.disableCart) && (
                <p className="product-information__dispatch-time">
                    <Translate value="general.dispatchTime" defaultValue="Dispatch time: {0}">
                        {t => formatString(t, prices.dispatchTime || '')}
                    </Translate>
                </p>
            )}
        </>
    );
};

const InputsSection = (props: IContactMontana) => {
    const {setContactMontanaModal} = props;

    const { config, cylindoImageUrl, prices, code, productTitleH1, dealers, useAssetImage, productImageUpdating, productPriceUpdating } = useProduct();
    const { addCartLine } = useCart();
    const { market, customerMode } = useSettings();
    const { links } = useDynamicSettings();
    const translate = useTranslations();
    const [amount, setAmount] = useState(1);
    const { trackAddToCart } = useTracking();

    const canBuy = useMemo(() => !market?.disableCart && !prices?.visibleNotBuyable, [market?.disableCart, prices?.visibleNotBuyable]);

    const addToCart = () => {
        if (!!prices?.codes?.length) {
            const port = location.port ? `:${location.port}` : '';
            const productToAdd: IProductRequest = {
                code: code || '',
                productCodes: prices.codes,
                productPageQueryString: config?.queryString,
                quantity: amount,
                productPageUrl: `${location.protocol}//${location.hostname}${port}${location.pathname}${config?.queryString}`
            };

            const gtmProducts: IGtmProductWithQuantity[] = [{
                id: code || '',
                name: productTitleH1 || '',
                price: prices.priceWithDiscount?.amount || 0,
                quantity: amount,
                ean: prices.ean
            }];
            trackAddToCart(prices.priceWithDiscount?.currency || '', gtmProducts);

            /**
             * `useAssetImage` determines if `imageUrl` should be added to the basket
             * line. The linked image is used to generate PDF receipts with images.
             * @author KRF
             * @see https://jira.impact.dk/browse/MON-3038
             */
            addCartLine(useAssetImage ? productToAdd : {...productToAdd, imageUrl: cylindoImageUrl});
        }
    };

    const options = [
        { value: '1', text: `1 ${translate(t => t.productPage.productPageItem)}` }
    ];

    for (let i = 2; i <= 10; i++) {
        options.push({ value: i.toString(), text: `${i} ${translate(t => t.productPage.productPageItems)}` });
    }

    return (
        <>
            <div className={clsx("product-information__buttons", canBuy && "product-information__buttons--can-buy")}>
                {customerMode === CUSTOMER_MODE.PROFESSIONAL &&
                    <Button
                        className="product-information__contact-montana-button"
                        text={translate(t => t.productPage.professionalsProductPageContactMontana, '[Contact Montana]')}
                        onClick={() => {
                            if (setContactMontanaModal) {
                                setContactMontanaModal(true)
                            }
                        }}
                    />
                }
                {(customerMode === CUSTOMER_MODE.PRIVATE || customerMode === CUSTOMER_MODE.B2B) &&
                    <>
                        {canBuy && (
                            <ButtonDropdown
                                className="product-information__add-to-bag-button"
                                label={translate(t => t.productPage.productPageAddToBag, '[Add to bag]')}
                                options={options}
                                isValueDisplayed={true}
                                onClick={addToCart}
                                onChange={(amount: number) => setAmount(amount)}
                                disabled={!prices?.codes?.length || (!useAssetImage && productImageUpdating) || productPriceUpdating}
                                isLoading={productPriceUpdating || productImageUpdating}
                                direction="up"
                            />
                        )}
                        {staticSettings.b2c &&
                            <Button
                                className={`product-information__find-dealer-button${market?.disableCart || prices?.visibleNotBuyable ? ' product-information__find-dealer-button--no-cart' : ''}`}
                                href={getFindDealerUrl(dealers, links)}
                                style={market?.disableCart ? ButtonStyles.Primary : ButtonStyles.Transparent}
                                text={translate(t => t.productPage.productPageFindDealer, '[Find dealer]')}
                            />
                        }
                    </>
                }
            </div>
        </>
    );
};

const USPSection = () => {

    const { properties, uspContractSpecific, uspContractSpecific2, uspContractSpecific3 } = useProduct();
    const { customerMode } = useSettings();

    return (
        <>
            {(customerMode === CUSTOMER_MODE.PROFESSIONAL && (uspContractSpecific || uspContractSpecific2 || uspContractSpecific3)) ?
                <ul className="product-information__usp">
                    <li className="product-information__usp__item">{uspContractSpecific}</li>
                    <li className="product-information__usp__item">{uspContractSpecific2}</li>
                    <li className="product-information__usp__item">{uspContractSpecific3}</li>
                </ul>
                :
                properties?.usPs && (
                    <ul className="product-information__usp">
                        {properties.usPs.map((usp, index) => (
                            <li key={`pi-usp-${index}`} className="product-information__usp__item">
                                {usp}
                            </li>
                        ))}
                    </ul>
                )
            }
        </>
    );


};

const DescriptionSection = () => {

    const { customerMode } = useSettings();
    const { productDescriptionLong, productDescriptionShort, contractDescription, designer, series, properties } = useProduct();
    const translate = useTranslations();
    const { scrollToTab } = usePDPContext();

    const description = customerMode === CUSTOMER_MODE.PROFESSIONAL
        ? (properties?.productDescriptionShort || contractDescription)
        : productDescriptionShort ?? productDescriptionLong;

    const lengthy = !productDescriptionShort && !!description && description.length > 130;

    return !!description ? (
        <>
            <p className="product-information__description">{lengthy ? `${description.slice(0, 130).trim()}...` : description}</p>
            <p className="product-information__description-detail">{`${translate(t => t.productPage.productPageDesigner, '[Designer]')}: ${designer?.designerName}`}</p>
            {customerMode === CUSTOMER_MODE.PROFESSIONAL &&
                <p className="product-information__description-detail">{`${translate(t => t.productPage.productPageSeries, '[Series]')}: ${series?.seriesName}`}</p>
            }
            <div className="product-information__description-ctas">
                <Button className="product-information__description-cta"
                    style={ButtonStyles.Text}
                    onClick={() => scrollToTab(PdpSpecificationsTab.DESCRIPTION)}
                    text={translate(t => t.productPage.productPageMoreInfoLink, '[Read more]')}
                />
                {customerMode === CUSTOMER_MODE.PRIVATE &&
                    <Button
                        className="product-information__downloads-cta product-information__downloads-left-margin"
                        onClick={() => scrollToTab(PdpSpecificationsTab.RESOURCES)}
                        style={ButtonStyles.Text}
                        text={translate(t => t.productPage.professionalsProductPageSeeMoreDownloads, '[See more downloads]')}
                    />
                }
            </div>
        </>
    ) : null;
};

interface IProductAttribute {
    label?: string | undefined;
    text?: string | undefined;
}
interface IDescriptionItemProps {
    variant: IProductAttribute;
    onClick?: () => void;
}

export const DescriptionItem: React.FC<IDescriptionItemProps> = React.memo(({ variant, onClick }: IDescriptionItemProps) => {
    const variantText: string = variant.text || '-';
    const textNodes: HTMLElement[] = [];

    const [textArray, setTextArray] = useState<string[]>([]);
    const [width, setWidth] = useState(0);

    const overlayRef = useRef<HTMLDivElement>(null);
    const [overlayClass, setOverlayClass] = useState("overlay-wipe");

    const translate = useTranslations();

    useEffect(() => {
        setTextArray([...textArray, variantText]);
    }, [variantText]);

    // Find the textNode that has the biggest width, so the overlay will always cover all text
    useEffect(() => {
        if (textArray.length > 1) {
            const maxWidth = textNodes
                .reduce((acc, node) => {
                    const width = node.getBoundingClientRect().width;
                    return (acc = width > acc ? width : acc);
                }, 0)

            // Add a bit of extra width to make sure the words are covered properly.
            setWidth(Math.ceil(maxWidth + 5));

            overlayRef.current && overlayRef.current.addEventListener("animationend", function listener() {
                setTextArray([variantText]);
                setOverlayClass("overlay-wipe overlay-wipe--reveal");
                overlayRef.current && overlayRef.current.removeEventListener("animationend", listener);
            });
            setOverlayClass("overlay-wipe overlay-wipe--cover");
        }
    }, [textArray]);

    return (
        <div className="product-information__configuration-item">
            <dt>{variant.label}</dt>
            <dd>
                {!!onClick && (
                    <Button
                        onClick={onClick}
                        style={ButtonStyles.Text}
                        className="product-information__configuration-button"
                    >
                        <>
                            {textArray.map((textString, i) => (
                                <span
                                    key={i}
                                    ref={node => node && textNodes.push(node)}
                                    className={`${i ? 'absolute opacity-0' : 'opacity-100'}`}
                                >
                                    {getDimensionTranslationValue(translate, textString)}
                                </span>
                            ))}
                            <div ref={overlayRef} className={`${overlayClass}`} style={{ width: width }} />
                        </>
                    </Button>
                )}
                {!onClick && (
                    <span style={{ whiteSpace: 'pre-line', position: 'relative' }}>
                        {textArray.map((textString, i) => (
                            <span
                                key={i}
                                ref={node => node && textNodes.push(node)}
                                className={`${i ? 'absolute opacity-0' : 'opacity-100'}`}
                            >
                                {getDimensionTranslationValue(translate, textString)}
                            </span>
                        ))}
                        <div ref={overlayRef} className={`${overlayClass}`} style={{ width: width }} />
                    </span>
                )}
            </dd>
        </div>
    )
});


export const DetailsSection = () => {

    const { config, measurements, showDepthAsHeight } = useProduct();
    const { setActiveVariationPicker } = usePDPContext();
    const translate = useTranslations();

    return (
        <>
            {!!config?.dimensions?.length && (
                <div className="product-information__configuration">
                    <dl>
                        {config.dimensions
                            .slice()
                            .sort((a, b) => preferredDimensionOrder.indexOf(a.dimensionName || '') - preferredDimensionOrder.indexOf(b.dimensionName || ''))
                            .map((dimension, index) =>
                                <DescriptionItem
                                    key={`prop-entry-${index}`}
                                    variant={{
                                        label: dimension.translationDimensionName,
                                        text: dimension.isColour
                                            ? dimension.displayValue
                                            : getDimensionTranslationValue(translate, dimension.translationDisplayValue)
                                    }}
                                    onClick={() => setActiveVariationPicker(dimension.dimensionName)}
                                />
                            )
                        }
                        {!!config.measurement && (
                            <DescriptionItem
                                key={`prop-entry-measurement`}
                                variant={{ label: showDepthAsHeight ? translate(t => t.productPage.productHeight, '[Height]') : translate(t => t.productPage.dimensionMeasurements, '[Depth]'), text: measurements?.find(m => m.seoFriendlyValue === config.measurement)?.displayValue }}
                                onClick={!!measurements && measurements.length > 1 ? () => setActiveVariationPicker('Measurements') : undefined}
                            />
                        )}
                    </dl>
                </div>
            )}
        </>
    );
};

const SpecificationsSection = () => {

    const { properties, prices, code, measurementRanges, baseModelMaterial1, baseModelMaterial2, baseModelMaterial3 } = useProduct();
    const translate = useTranslations();
    const { customerMode } = useSettings();

    const materialOne = customerMode === CUSTOMER_MODE.PROFESSIONAL ? baseModelMaterial1 : prices?.materialOne;
    const materialTwo = customerMode === CUSTOMER_MODE.PROFESSIONAL ? baseModelMaterial2 : prices?.materialTwo;
    const materialThree = customerMode === CUSTOMER_MODE.PROFESSIONAL ? baseModelMaterial3 : prices?.materialThree;


    return (
        <>
            {properties && (
                <div className="product-information__configuration">
                    <dl>
                        {!!properties.designer && (
                            <DescriptionItem variant={{ label: translate(t => t.productPage.productPageDesigner, '[Designer]'), text: properties.designer }} />
                        )}
                        {!!materialOne && (
                            <DescriptionItem variant={{ label: translate(t => t.productPage.productPageMaterial, '[Material]'), text: materialOne }} />
                        )}
                        {!!materialTwo && (
                            <DescriptionItem variant={{ label: '', text: materialTwo }} />
                        )}
                        {!!materialThree && (
                            <DescriptionItem variant={{ label: '', text: materialThree }} />
                        )}
                        {customerMode === CUSTOMER_MODE.PROFESSIONAL && measurementRanges && (
                            measurementRanges.map((measurementRange, index) => {
                                return (
                                    <DescriptionItem key={`product-page-measurementRange-${index}`} variant={{ label: index === 0 ? translate(t => t.productPage.productPageMeasurements, '[Measurements]' ) : '', text: measurementRange }} />
                                )
                            })
                        )}
                        {customerMode === CUSTOMER_MODE.PRIVATE && !!prices?.measurements && (
                            <DescriptionItem variant={{ label: translate(t => t.productPage.productPageMeasurements, '[Measurements]'), text: prices.measurements }} />
                        )}
                        {!!code && (
                            <DescriptionItem variant={{ label: translate(t => t.productPage.productPageItemNumber, '[Item number]'), text: code }} />
                        )}
                        {!!prices?.ean && (
                            <DescriptionItem variant={{ label: translate(t => t.productPage.productEAN, '[Ean]'), text: prices.ean }} />
                        )}
                    </dl>
                </div>
            )}
        </>
    );
};

const SeeMoreDownloads = () => {
    const { customerMode } = useSettings();
    if (customerMode === CUSTOMER_MODE.PROFESSIONAL) {
        return <></>;
    }

    const translate = useTranslations();
    const { scrollToTab } = usePDPContext();

    return (
        <div className="product-information__downloads-link">
            <SvgIcon iconName={'arrow_down'}/>
            <Button
                className="product-information__downloads-cta"
                onClick={() => scrollToTab(PdpSpecificationsTab.RESOURCES)}
                style={ButtonStyles.Text}
                text={translate(t => t.productPage.professionalsProductPageSeeMoreDownloads, '[See more downloads]')}
            />
        </div>
    )
}

const ResourceLinks = () => {
    const { customerMode } = useSettings();
    if (customerMode === CUSTOMER_MODE.PRIVATE || customerMode === CUSTOMER_MODE.B2B) {
        return <></>;
    }

    const translate = useTranslations();
    const {productSheet, properties} = useProduct();
    const { scrollToTab } = usePDPContext();

    const [m22BlockId, setM22BlockId] = useState<any>();

    useEffect(() => {
        setM22BlockId(document.getElementById("colours-and-surfaces"));
    }, [])

    return (
        <div className='product-information__downloads'>
            {productSheet &&
                <div className="product-information__downloads-link">
                    <SvgIcon iconName={'download'}/>
                    <Button
                        className="product-information__downloads-cta"
                        href={productSheet}
                        target="_blank"
                        style={ButtonStyles.Text}
                        text={translate(t => t.productPage.professionalsProductPageDownloadProductsheet, '[Download productsheet]')}
                    />
                </div>
            }
            {properties?.fileLink3DModel &&
                <div className="product-information__downloads-link">
                    <SvgIcon iconName={'download'}/>
                    <Button
                        className='product-information__downloads-cta'
                        href={properties?.fileLink3DModel}
                        target="_blank"
                        style={ButtonStyles.Text}
                        text={translate(t => t.productPage.professionalsProductPageDownload3dFiles, '[Download 3D files]')}
                    />
                </div>
            }
            <div className="product-information__downloads-link">
                <SvgIcon iconName={'arrow_down'}/>
                <Button
                    className="product-information__downloads-cta"
                    onClick={() => scrollToTab(PdpSpecificationsTab.RESOURCES)}
                    style={ButtonStyles.Text}
                    text={translate(t => t.productPage.professionalsProductPageSeeMoreDownloads, '[See more downloads]')}
                />
            </div>
            {m22BlockId &&
                <div className="product-information__downloads-link">
                    <SvgIcon iconName={'arrow_down'}/>
                    <Button
                        className="product-information__downloads-cta"
                        onClick={() => {m22BlockId.scrollIntoView({ behavior: 'smooth' })}}
                        style={ButtonStyles.Text}
                        text={translate(t => t.productPage.professionalsProductPageViewColoursAndSurfaces, '[View colours and surfaces]')}
                    />
                </div>
            }
        </div>
    )
}

const VATExcludedSection = () => {

    const { professionalsFromPrice } = useProduct();
    const { market } = useSettings();

    return (
        <>
            {(!!professionalsFromPrice && !market?.hidePrices) && (
                <p className="product-information__vat-excluded">
                    <Translate value="productPage.professionalsProductPageVatExcluded" defaultValue="[The price is excl. VAT]" />
                </p>
            )}
        </>
    );
};

export const ConfigurationMobile: React.FC = React.memo((): JSX.Element | null => {
    const { config, measurements } = useProduct();
    const translate = useTranslations();

    const descriptionItem = (dimension: IProductConfigOption): string | undefined => {
        if (dimension.condition === 'base type:legs') {
            return `${getDimensionTranslationValue(translate, dimension.translationDisplayValue)}`;
        }

        return getDimensionTranslationValue(translate, dimension.translationDisplayValue);
    }

    return (
        <>
            {!!config?.dimensions?.length &&
            <ul className="product-information__configuration-mobile">
                {config.dimensions
                    .slice()
                    .sort((a, b) => preferredDimensionOrder.indexOf(a.dimensionName || '') - preferredDimensionOrder.indexOf(b.dimensionName || ''))
                    .map((dimension, index) =>
                        <li key={`prop-entry-${index}`}>
                            {`${dimension.translationDimensionName}: `}
                            {dimension.isColour
                                ? dimension.displayValue
                                : descriptionItem(dimension)
                            }
                        </li>
                    )
                }
                {!!config.measurement && (
                    <li key={`prop-entry-measurement`}>
                        {measurements?.find(m => m.seoFriendlyValue === config.measurement)?.displayValue}
                    </li>
                )}
            </ul>
            }
        </>
    );
})

export const ProductInformation: React.FC<IContactMontana> = React.memo((props: IContactMontana): JSX.Element | null => {
    const {setContactMontanaModal} = props;
    const { config, productTitleH1, prices } = useProduct();
    const translate = useTranslations();

    if (!config) {
        return null;
    }

    const tabs: ITabProps[] = [
        {
            header: translate(t => t.productPage.productPageDescription, '[Description]'),
            idPrefix: "information",
            content: <><DescriptionSection /><ResourceLinks /></>
        },
        {
            header: translate(t => t.productPage.productPageSpecifications, '[Specifications]'),
            idPrefix: "information",
            content: <><SpecificationsSection /><DetailsSection /><ResourceLinks /><SeeMoreDownloads /></>
        },
    ];

    const b2bTabs: ITabProps[] = [
        {
            header: translate(t => t.productPage.productPageConfiguration, '[Configuration]'),
            idPrefix: "information",
            content: <><DetailsSection /><SpecificationsSection /></>
        },
        {
            header: translate(t => t.productPage.productPageDescription, '[Description]'),
            idPrefix: "information",
            content: <DescriptionSection />
        }
    ];

    const mobileTabs: ITabProps[] = [
        {
            header: translate(t => t.productPage.productPageDescription, '[Description]'),
            idPrefix: "information",
            content: <><DescriptionSection /><ResourceLinks /></>
        },
        {
            header: translate(t => t.productPage.productPageSpecifications, '[Specifications]'),
            idPrefix: "information",
            content: <><SpecificationsSection /><DetailsSection /><ResourceLinks /><SeeMoreDownloads /></>
        },
    ];

    const { customerMode } = useSettings();

    return (
        <>
            {(customerMode === CUSTOMER_MODE.PRIVATE || customerMode === CUSTOMER_MODE.B2B) &&
                <div className="product-information">
                    <SmallerThanTabletLandscape includeSSR>
                        <div className="product-information__column product-information__column--first">
                            <div>
                                <h2 className="product-information__title">{productTitleH1}</h2>
                                <ConfigurationMobile/>
                                {prices?.splash &&
                                    <DispatchTimeSection />
                                }
                            </div>
                            <div>
                                <PriceSection />
                                {!prices?.splash &&
                                    <DispatchTimeSection />
                                }
                            </div>
                            <InputsSection />
                            <USPSection />
                        </div>
                        <div className="product-information__column">
                            <Tablist tabs={staticSettings.b2b ? b2bTabs : mobileTabs} />
                        </div>
                    </SmallerThanTabletLandscape>

                    <TabletLandscapeAndLarger>
                        <div>
                            <h2 className="product-information__title">{productTitleH1}</h2>
                            <Tablist tabs={staticSettings.b2b ? b2bTabs : tabs} />
                        </div>
                        <div>
                            <PriceSection />
                            <DispatchTimeSection />
                            <InputsSection />
                            <USPSection />
                        </div>
                    </TabletLandscapeAndLarger>
                </div>
            }
            {customerMode === CUSTOMER_MODE.PROFESSIONAL &&
                <div className="product-information">
                    <SmallerThanTabletLandscape includeSSR>
                        <div className="product-information__column product-information__column--first">
                            <div>
                                <h2 className="product-information__title">{productTitleH1}</h2>
                                <ConfigurationMobile/>
                            </div>
                            <div>
                                <PriceSection />
                                <VATExcludedSection />
                            </div>
                            <InputsSection setContactMontanaModal={setContactMontanaModal} />
                            <USPSection />
                        </div>
                        <div className="product-information__column">
                            <Tablist tabs={staticSettings.b2b ? b2bTabs : mobileTabs} />
                        </div>
                    </SmallerThanTabletLandscape>

                    <TabletLandscapeAndLarger>
                        <div>
                            <h2 className="product-information__title">{productTitleH1}</h2>
                            <Tablist tabs={staticSettings.b2b ? b2bTabs : tabs} />
                        </div>
                        <div>
                            <PriceSection />
                            <VATExcludedSection />
                            <InputsSection setContactMontanaModal={setContactMontanaModal} />
                            <USPSection />
                        </div>
                    </TabletLandscapeAndLarger>
                </div>
            }
        </>
    );
});
